<template>
  <div class="dropzone-field">
    <div
      v-if="title"
      class="dropzone-field__title"
    >
      {{ title }}
    </div>
    <div
      ref="zone"
      class="dropzone"
      :class="`dropzone--${currentState}`"
      @dragenter.prevent="onDragEnter"
      @dragover.prevent="onDragEnter"
      @dragleave.prevent="onDragLeave"
      @drop.prevent="onDrop"
    >
      <input
        ref="input"
        type="file"
        class="dropzone__input"
        :accept="accept"
        :multiple="fileLimit > 1"
        :disabled="isUploading"
        @change="onFileInputChange"
      >
      <div class="dropzone__icon">
        <template v-if="currentState === 'loaded'">
          ✓
        </template>
        <template v-else-if="currentState === 'error'">
          ✕
        </template>
        <template v-else>
          ⇧
        </template>
      </div>
      <div
        class="dropzone__status"
        aria-live="polite"
      >
        <div class="dropzone__label">
          <template v-if="currentState === 'uploading'">
            Enviando…
          </template>
          <template v-else-if="currentState === 'loaded'">
            {{ displayName }}
          </template>
          <template v-else-if="currentState === 'error'">
            {{ errorTitle }}
          </template>
          <template v-else-if="currentState === 'hover'">
            Solte para enviar
          </template>
          <template v-else>
            {{ instructionText }}
          </template>
        </div>
        <div class="dropzone__sub">
          <template v-if="currentState === 'uploading'">
            {{ displayName }}
          </template>
          <template v-else-if="currentState === 'loaded'">
            {{ displaySize }} · {{ uploadFailed ? 'falha no envio, tente novamente' : 'pronto' }}
          </template>
          <template v-else-if="currentState === 'error'">
            {{ errorMessage }}
          </template>
          <template v-else>
            {{ idleSubText }}
          </template>
        </div>
      </div>
      <button
        v-if="currentState === 'loaded' || currentState === 'error'"
        type="button"
        class="dropzone__remove"
        aria-label="Remover arquivo"
        title="Remover arquivo"
        @click.stop="clear"
      >
        ✕
      </button>
    </div>
    <p
      v-if="warnMessage"
      class="dropzone-field__warn"
    >
      {{ warnMessage }}
    </p>
    <div
      v-if="showUploadButton"
      class="dropzone-field__actions"
    >
      <BaseButtonAction
        :label="action.label || 'Enviar'"
        color="primary"
        :is-disabled="isUploading"
        @click.native="startUpload"
      />
    </div>
  </div>
</template>

<script>
import BaseButtonAction from './BaseButtonAction.vue'

const SIZE_UNITS = { B: 1, KB: 1024, MB: 1024 ** 2, GB: 1024 ** 3 }

export default {
  name: 'BaseDropzone',
  components: { BaseButtonAction },
  props: {
    title: {
      type: String,
      required: false,
      default: ''
    },
    accept: {
      type: String,
      required: false,
      default: ''
    },
    instructionText: {
      type: String,
      required: false,
      default: 'Arraste o arquivo aqui'
    },
    subText: {
      type: String,
      required: false,
      default: ''
    },
    warnMessage: {
      type: String,
      required: false,
      default: ''
    },
    maxSize: {
      type: String,
      required: false,
      default: ''
    },
    multiple: {
      type: Boolean,
      required: false,
      default: false
    },
    maxFiles: {
      type: Number,
      required: false,
      default: null
    },
    action: {
      type: Object,
      required: false,
      default: null
    },
    fileName: {
      type: String,
      required: false,
      default: ''
    },
    fileSize: {
      type: String,
      required: false,
      default: ''
    }
  },
  data() {
    return {
      isDragging: false,
      selectedFiles: [],
      errorTitle: '',
      errorMessage: '',
      isUploading: false,
      uploadFailed: false
    }
  },
  computed: {
    displayName() {
      if (this.selectedFiles.length > 1) {
        return `${this.selectedFiles.length} arquivos selecionados`
      }
      return this.selectedFiles.length ? this.selectedFiles[0].name : this.fileName
    },
    displaySize() {
      if (!this.selectedFiles.length) {
        return this.fileSize
      }
      const totalBytes = this.selectedFiles.reduce((total, file) => total + file.size, 0)
      return this.formatFileSize(totalBytes)
    },
    idleSubText() {
      return [this.subText, this.accept, this.maxSize && `até ${this.maxSize}`].filter(Boolean).join(' · ')
    },
    acceptedRules() {
      return this.accept
        .split(',')
        .map((rule) => rule.trim().toLowerCase())
        .filter(Boolean)
    },
    fileLimit() {
      if (!this.multiple) {
        return 1
      }
      return this.maxFiles || Infinity
    },
    maxSizeBytes() {
      const match = /^\s*(\d+(?:[.,]\d+)?)\s*(B|KB|MB|GB)?\s*$/i.exec(this.maxSize || '')
      if (!match) {
        return null
      }
      const unit = (match[2] || 'B').toUpperCase()
      return Number(match[1].replace(',', '.')) * SIZE_UNITS[unit]
    },
    hasActionListener() {
      return Boolean(this.$listeners.action)
    },
    showUploadButton() {
      return Boolean(this.action) && this.hasActionListener && this.selectedFiles.length > 0 && !this.errorMessage
    },
    currentState() {
      if (this.errorMessage) {
        return 'error'
      }
      if (this.isUploading) {
        return 'uploading'
      }
      if (this.displayName) {
        return 'loaded'
      }
      return this.isDragging ? 'hover' : 'idle'
    }
  },
  created() {
    if (process.env.NODE_ENV !== 'production' && this.action && !this.hasActionListener) {
      // eslint-disable-next-line no-console
      console.warn(
        `BaseDropzone: "${this.title || this.instructionText}" tem "action" mas ninguém escuta o evento "action" — o botão "Enviar" fica oculto, senão o campo ficaria preso em "Enviando…".`
      )
    }
  },
  methods: {
    onDragEnter() {
      if (this.isUploading) {
        return
      }
      this.isDragging = true
      this.errorMessage = ''
    },
    onDragLeave(event) {
      if (event.relatedTarget && this.$refs.zone.contains(event.relatedTarget)) {
        return
      }
      this.isDragging = false
    },
    onDrop(event) {
      this.isDragging = false
      if (this.isUploading) {
        return
      }
      this.handleFiles(event.dataTransfer.files)
    },
    onFileInputChange(event) {
      this.handleFiles(event.target.files)
    },
    handleFiles(fileList) {
      const files = Array.from(fileList || [])
      if (!files.length) {
        return
      }
      const error = this.validateFiles(files)
      this.uploadFailed = false
      if (error) {
        this.selectedFiles = []
        this.errorTitle = error.title
        this.errorMessage = error.message
        return
      }
      this.errorMessage = ''
      this.selectedFiles = files
      this.$emit('file-selected', this.fileLimit === 1 ? files[0] : files)
    },
    validateFiles(files) {
      if (files.length > this.fileLimit) {
        const message = this.fileLimit === 1 ? 'Selecione apenas 1 arquivo.' : `Selecione até ${this.fileLimit} arquivos.`
        return { title: 'Limite de arquivos', message }
      }
      const notAccepted = files.find((file) => !this.isFileAccepted(file))
      if (notAccepted) {
        return { title: 'Arquivo não permitido', message: `Formato não permitido. Aceitos: ${this.accept}` }
      }
      const tooLarge = this.maxSizeBytes && files.find((file) => file.size > this.maxSizeBytes)
      if (tooLarge) {
        return { title: 'Arquivo não permitido', message: `${tooLarge.name} excede o tamanho máximo de ${this.maxSize}.` }
      }
      return null
    },
    startUpload() {
      if (!this.action || !this.hasActionListener || !this.selectedFiles.length || this.isUploading) {
        return
      }
      this.isUploading = true
      this.uploadFailed = false
      this.$emit('action', {
        action: this.action,
        files: this.selectedFiles,
        onSettled: this.onUploadSettled
      })
    },
    onUploadSettled({ failedFiles = [] } = {}) {
      this.isUploading = false
      if (failedFiles.length) {
        this.selectedFiles = failedFiles
        this.uploadFailed = true
        return
      }
      this.reset()
    },
    clear() {
      this.reset()
      this.$emit('cleared')
    },
    reset() {
      this.selectedFiles = []
      this.errorMessage = ''
      this.uploadFailed = false
      this.resetInput()
    },
    resetInput() {
      if (this.$refs.input) {
        this.$refs.input.value = ''
      }
    },
    isFileAccepted(file) {
      if (!this.acceptedRules.length) {
        return true
      }
      const fileName = file.name.toLowerCase()
      const fileType = (file.type || '').toLowerCase()
      return this.acceptedRules.some((rule) => {
        if (rule.startsWith('.')) {
          return fileName.endsWith(rule)
        }
        if (rule.endsWith('/*')) {
          return fileType.startsWith(rule.replace('/*', '/'))
        }
        return fileType === rule
      })
    },
    formatFileSize(bytes) {
      if (bytes < 1024) {
        return `${bytes} B`
      }
      const units = ['KB', 'MB', 'GB']
      let value = bytes / 1024
      let unitIndex = 0
      while (value >= 1024 && unitIndex < units.length - 1) {
        value /= 1024
        unitIndex += 1
      }
      return `${value.toFixed(1).replace('.', ',')} ${units[unitIndex]}`
    }
  }
}
</script>

<style lang="scss">
.dropzone-field {
  &__title {
    font-size: 0.875rem;
    font-weight: 600;
    margin-bottom: 8px;
    color: var(--v-textPrimary-base);
  }

  &__warn {
    margin: 8px 0 0;
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--ds-color-black-055);
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 12px;
  }
}

.dropzone {
  position: relative;
  border: 2px dashed var(--ds-color-black-010);
  border-radius: 8px;
  padding: 26px 16px;
  text-align: center;
  background: var(--ds-color-black-005);

  &__input {
    position: absolute;
    inset: 0;
    opacity: 0;
    cursor: pointer;
    width: 100%;
    height: 100%;

    &:disabled {
      cursor: progress;
    }
  }

  &__icon {
    font-size: 22px;
    margin-bottom: 8px;
    color: var(--ds-color-black-040);
  }

  &__label {
    font-size: 0.875rem;
    font-weight: 600;
    margin-bottom: 2px;
    color: var(--v-textPrimary-base);
  }

  &__sub {
    font-size: 0.8rem;
    color: var(--ds-color-black-040);
  }

  &__remove {
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 2;
    width: 22px;
    height: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 700;
    line-height: 1;
    color: var(--v-error-base);
    background: var(--ds-color-white);
    border: 1px solid var(--ds-color-black-010);
    border-radius: 50%;
    cursor: pointer;
    padding: 0;

    &:hover {
      background: rgba(235, 104, 104, 0.12);
      border-color: var(--v-error-base);
    }
  }

  &--hover,
  &--uploading {
    border-color: var(--v-bluefa-base);
    background: rgba(53, 168, 250, 0.08);

    .dropzone__icon {
      color: var(--v-bluefa-base);
    }
  }

  &--loaded {
    border-color: var(--v-success-base);
    background: rgba(76, 175, 80, 0.08);

    .dropzone__icon {
      color: var(--v-success-base);
    }
  }

  &--error {
    border-color: var(--v-error-base);
    background: rgba(235, 104, 104, 0.08);

    .dropzone__icon {
      color: var(--v-error-base);
    }

    .dropzone__label {
      color: var(--v-error-base);
    }
  }
}
</style>
